import { html } from "lit";
import { customElement, property, query } from "lit/decorators.js";
import { MenuEvent, dom } from "@omegagrid/core";
import { msg } from "@omegagrid/localize";
import rp, { Browser, BrowserEvent, BrowserState, BaseSidebar } from "@reporting/rp-app-host";
import { AccountingRights } from "@reporting/rp-types/core";
import { style } from "./sidebar.style";
import { PageBrowserAdapter, IdeBrowserAdapter } from "../adapters";
import { ALLOWED_LANGS, DEFAULT_IDE_LANG, TreeItem, WikiTypes } from "../types";
import { buildWikiDownloadUrl, isImage } from "../helpers";
import { WikiApplication } from "..";

@customElement('rp-wiki-sidebar')
export class Sidebar extends BaseSidebar<WikiApplication> {

	static styles = [...BaseSidebar.styles, style];

	@property({ type: Boolean })
	customWiki: boolean;

	@property({ type: Boolean })
	ideMode: boolean = false;

	@property({ type: String })
	ideLang: string;

	@query('rp-browser')
	browser: Browser;

	get activeAccountingId(): number {
		return this.customWiki ? rp.accountingProvider.a : 0;
	}

	connectedCallback() {
		super.connectedCallback();
		this.ideLang = rp.host.container.userProvider?.user?.lang ?? DEFAULT_IDE_LANG;
		this.customWiki = (this.state?.customWiki as boolean) ?? false;
	}

	firstUpdated() {
		this.browser.addEventListener('browser.state', this._onChangeState);
		this.browser.state = this.state?.wikiBrowser as BrowserState;
	}

	getState() {
		return {
			name: 'sidebar',
			wikiBrowser: this.browser?.getState(),
			customWiki: this.customWiki,
		}
	}

	_onChangeState = () => this.dispatchStateEvent();

	refresh = () => this.browser.refresh();

	getLangItems = () => {
		return ALLOWED_LANGS.map(lang => ({ key: lang, value: lang }));
	}

	toggleCollapse = () => this.browser.tree?.toggleTreeCollapse();

	switchMode = () => this.ideMode = !this.ideMode;

	_onSelect = (e: BrowserEvent) => {
		if (e.node.key.includes('Form')) {
			rp.host.openModalSidebar({ id: e.node.key, caption: msg(`Create Translation - ${e.node.value}`) });
		} else if (e.node.key.includes('wiki')) {
			rp.host.open(
				e.node.key,
				{ title: `${this.ideMode && (e.node.properties.item as TreeItem).type == 'page' ? '['+this.ideLang+'] ' : ''}${e.node.value}` }
			);
		} else if (isImage(e.node.value)) {
			const component = dom.createElement('div');
			component.style.padding = '16px';
			component.innerHTML = `<img src="${buildWikiDownloadUrl(this.activeAccountingId, e.node.key)}"
				style="width: 100%; height: 100%;"></img>`;
			rp.host.openDialog({
				component,
				caption: e.node.value,
				closable: true,
				resizable: true,
				maximizable: true,
				scrollable: true,
				location: 'center',
				width: 666,
				height: 490
			});
		} else {
			window.open(buildWikiDownloadUrl(this.activeAccountingId, e.node.key), '_blank');
		}
	}

	_onNewMenuItemSelect = (e: MenuEvent) => {
		switch (e.item.key) {
			case 'newPage':
				rp.host.openModalSidebar({ id: `wiki-pageForm-${this.activeAccountingId}-${this.ideLang}`, caption: msg('Create new page') });
				break;
			case 'newFile':
				rp.host.openModalSidebar({ id: `wiki-fileForm-${this.activeAccountingId}`, caption: msg('Create new file') });
				break;
		}
	}

	_onLangSelect = (e: MenuEvent) => {
		this.ideLang = e.item.key as string;
	}

	_onMenuSelect = (e: MenuEvent) => {
		switch (e.item.key) {
			case 'vectorStore':
				rp.host.open(`wiki-vectors-${this.activeAccountingId}`, { title: msg('Vector store') });
				break;
			case 'news':
				rp.host.open('wiki-messages', { title: msg('News') });
				break;
			case 'sync':
				rp.host.open('wiki-sync', { title: `${msg('Wiki')}: ${msg('Synchronization')}` });
				break;
		}

	}

	_onWikiSelect = (type: WikiTypes) => {
		this.customWiki = type == WikiTypes.custom;
		if (type == WikiTypes.app && !rp.userProvider.isSA()) this.ideMode = false;
		this.dispatchStateEvent();
	}

	/** Shows the given wiki (the application's or the company's) in the tree, as the head buttons do. */
	showWiki = (type: WikiTypes) => this._onWikiSelect(type);

	render = () => html`
		${rp.userProvider.isGuest() ? '' : html`
			<div class="head">
				<og-bucket-button
					text="${msg('wiki-app')}"
					icon="book"
					@click="${() => this._onWikiSelect(WikiTypes.app)}"
					?active="${!this.customWiki}">
				</og-bucket-button>

				<og-bucket-button
					text="${msg('wiki-custom')}"
					icon="book-user"
					@click="${() => this._onWikiSelect(WikiTypes.custom)}"
					?active="${this.customWiki}">
				</og-bucket-button>
			</div>
		`}

		<div class="controls">
			<og-button
				icon="search"
				color="transparent"
				text="${msg('Search')}"
				style="flex: 1"
				@click="${() => rp.host.openCommands('wiki')}">
			</og-button>

			<og-button
				icon="arrows-rotate"
				color="transparent"
				title="${msg('Refresh')}"
				@click="${() => this.refresh()}">
			</og-button>

			<og-button
				icon="square-plus"
				color="transparent"
				title="${msg('Collapse/Expand tree')}"
				@click="${() => this.toggleCollapse()}">
			</og-button>

			${(this.customWiki && rp.userProvider.hasRight(AccountingRights.WikiWrite)) || rp.userProvider.isSA() ? html`
				${this.ideMode ? html`
					<og-dropdown-menu
						noCaret
						dynamicWidth
						color="transparent"
						title="${msg('Lang')}"
						style="flex: 0 0 25px; text-align: center;"
						@menu.select="${this._onLangSelect}"
						.items="${this.getLangItems()}">
						<div slot="label" id="lang">${this.ideLang}</div>
					</og-dropdown-menu>

					<og-dropdown-menu
						noCaret
						dynamicWidth
						color="transparent"
						icon="plus"
						title="${msg('New')}"
						style="flex: 0 0 25px; text-align: center;"
						@menu.select="${this._onNewMenuItemSelect}"
						.items="${[
							{key: 'newPage', value: msg('New page'), icon: 'file-lines'},
							{key: 'newFile', value: msg('New file'), icon: 'file'},
						]}">
					</og-dropdown-menu>

					<og-dropdown-menu
						noCaret
						dynamicWidth
						style="min-width: 25px;"
						icon="bars"
						color="transparent"
						@click="${(e: PointerEvent) => e.stopImmediatePropagation()}"
						@menu.select="${this._onMenuSelect}"
						.items="${[
							{key: 'vectorStore', value: msg('Vector store'), icon: 'vector-polygon'},
							rp.userProvider.isSA() && !this.customWiki ? {key: 'news', value: msg('News'), icon: 'newspaper'} : null,
							this.customWiki ? {key: 'sync', value: msg('Synchronization'), icon: 'arrows-spin'} : null,
						].filter(Boolean)}">
					</og-dropdown-menu>
				` : ''}

				<og-button
					icon="${this.ideMode ? 'arrow-left-from-bracket' : 'pencil'}"
					color="transparent"
					title="${this.ideMode ? msg('Exit edit mode') : msg('Enter edit mode')}"
					@click="${() => this.switchMode()}">
				</og-button>
			` : ''}
		</div>

		<div class="body">
			<rp-browser
				id="browser"
				.adapter="${this.ideMode ? new IdeBrowserAdapter(this.activeAccountingId, this.ideLang) : new PageBrowserAdapter(this.activeAccountingId)}"
				@browser.select="${this._onSelect}">
			</rp-browser>
		</div>
	`;

}
